/*
 * Trust Sections - social proof for the landing page.
 *
 * Four quiet, verifiable blocks after the feature showcase:
 *
 *   trusted brands marquee → built-by logo wall → heritage claim
 *   (with a public-record "verify" link) → Clutch reviews
 *
 * Logos and review data come from fxis.ai (the lab behind 8080.AI)
 * and its independently verified Clutch profile.
 */
import React from 'react';

import './TrustSections.css';

const FXIS_URL = 'https://fxis.ai/';
const CLUTCH_URL = 'https://clutch.co/profile/fxisai';
const ZAUBA_URL = 'https://www.zaubacorp.com/F-X-DATA-LABS-PRIVATE-LIMITED-U72300GJ2016PTC085697';

const asset = (path: string): string => `${process.env.PUBLIC_URL || ''}${path}`;

interface LogoDef {
  file: string;
  name: string;
}

/* Brands that have worked with fxis.ai (fxis.ai "Trusted By Leading Brands"). */
const TRUSTED_ROW_A: LogoDef[] = [
  { file: 'jnj', name: 'Johnson & Johnson' },
  { file: 'symphony', name: 'Symphony' },
  { file: 'guinness-world-records', name: 'Guinness World Records' },
  { file: 'gmr', name: 'GMR Group' },
  { file: 'up-government', name: 'Government of Uttar Pradesh' },
  { file: 'bakeri', name: 'Bakeri Group' },
  { file: 'givelify', name: 'Givelify' },
  { file: 'rocketreach', name: 'RocketReach' },
  { file: 'paravision', name: 'Paravision' },
  { file: 'iifl-ahimsa-run', name: 'IIFL Ahimsa Run' },
];

const TRUSTED_ROW_B: LogoDef[] = [
  { file: 'govt-assam', name: 'Government of Assam' },
  { file: 'gujarat-yog-board', name: 'Gujarat State Yog Board' },
  { file: 'pareit', name: 'PareIT' },
  { file: 'capital-numbers', name: 'Capital Numbers' },
  { file: 'globhe', name: 'Globhe' },
  { file: 'sumtracker', name: 'Sumtracker' },
  { file: 'weteam', name: 'We.Team' },
  { file: 'algo', name: 'Algo' },
  { file: 'yesreferral', name: 'YesReferral' },
  { file: 'supersourcing', name: 'Supersourcing' },
  { file: 'solaris-finance', name: 'Solaris Finance' },
];

/* Companies our engineers have previously worked at (fxis.ai "Hire Ex Experts From"). */
const BUILDER_LOGOS: LogoDef[] = [
  { file: 'amazon', name: 'Amazon' },
  { file: 'johnson-johnson', name: 'Johnson & Johnson' },
  { file: 'accenture', name: 'Accenture' },
  { file: 'nokia', name: 'Nokia' },
  { file: 'tcs', name: 'Tata Consultancy Services' },
  { file: 'jio', name: 'Jio' },
  { file: 'adani', name: 'Adani' },
  { file: 'crisil', name: 'CRISIL' },
  { file: 'bitcoin-com', name: 'Bitcoin.com' },
  { file: 'rci-drdo', name: 'RCI – DRDO' },
  { file: 'brilworks', name: 'Brilworks' },
  { file: 'zidisha', name: 'Zidisha' },
];

interface ReviewDef {
  quote: string;
  detail: string;
  author: string;
}

/* Verified client reviews from clutch.co/profile/fxisai (all rated 5.0). */
const REVIEWS: ReviewDef[] = [
  {
    quote: 'They are truly an incredible company.',
    detail: 'Improved platform usability and AI model accuracy, with exceptional project management and on-time delivery.',
    author: 'CEO, AI medical platform · Los Angeles, CA',
  },
  {
    quote: 'Ability to execute at a high standard on genuinely complex problems.',
    detail: 'Refactored our LLM architecture and shipped new features with clear progress tracking and patient technical explanations.',
    author: 'Chief Strategy & Operating Officer, mental health app · Newark, DE',
  },
  {
    quote: 'They bridge business needs with technical execution.',
    detail: 'Delivered a 30–40% reduction in manual tasks and a 20–25% improvement in lead engagement.',
    author: 'Founder, agriculture company · Ahmedabad, IN',
  },
  {
    quote: 'They understood both business and technology.',
    detail: 'Built a voice AI system that handles customer calls after hours, cutting missed leads and routine phone time.',
    author: 'Franchise owner, pet services · Chantilly, VA',
  },
  {
    quote: 'Delivered insights reshaping how we plan campaigns.',
    detail: 'An AI-powered marketing suite that improved campaign response rates by 55% and lead conversion speed by 30%.',
    author: 'CEO, creative marketing agency · Florida, US',
  },
  {
    quote: 'Built AI tools that felt practical, not technical.',
    detail: 'A recruitment chatbot that reduced manual screening workload by over 60% with real-time candidate conversations.',
    author: 'HR manager, staffing company · India',
  },
];

const SectionHeader: React.FC<{
  eyebrow: string;
  headingId: string;
  title: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ eyebrow, headingId, title, sub }) => (
  <div className="ts-header">
    <p className="ts-header__eyebrow">{eyebrow}</p>
    <h2 id={headingId} className="ts-header__heading">
      {title}
      {sub && (
        <>
          <br />
          <span className="ts-header__sub">{sub}</span>
        </>
      )}
    </h2>
  </div>
);

const LogoChip: React.FC<{ logo: LogoDef }> = ({ logo }) => (
  <div className="ts-chip" title={logo.name}>
    <img src={asset(`/logos/${logo.file}.png`)} alt={logo.name} loading="lazy" />
  </div>
);

const MarqueeRow: React.FC<{ logos: LogoDef[]; reverse?: boolean }> = ({ logos, reverse }) => (
  <div className="ts-marquee">
    <div className={`ts-marquee__track${reverse ? ' ts-marquee__track--reverse' : ''}`}>
      <div className="ts-marquee__group">
        {logos.map((logo) => (
          <LogoChip key={logo.file} logo={logo} />
        ))}
      </div>
      {/* duplicate list makes the loop seamless */}
      <div className="ts-marquee__group" aria-hidden="true">
        {logos.map((logo) => (
          <LogoChip key={`dup-${logo.file}`} logo={logo} />
        ))}
      </div>
    </div>
  </div>
);

const TrustedBrandsSection: React.FC = () => (
  <section id="trust" className="ts-section" aria-labelledby="trusted-brands-heading">
    <div className="ts-inner ts-inner--wide">
      <SectionHeader
        eyebrow="Trusted"
        headingId="trusted-brands-heading"
        title="Trusted by leading brands."
        sub="Fortune 500s, governments and startups trust the team behind 8080.AI."
      />
      <MarqueeRow logos={TRUSTED_ROW_A} />
      <MarqueeRow logos={TRUSTED_ROW_B} reverse />
    </div>
  </section>
);

const BuiltBySection: React.FC = () => (
  <section className="ts-section" aria-labelledby="built-by-heading">
    <div className="ts-inner">
      <SectionHeader
        eyebrow="The team"
        headingId="built-by-heading"
        title="Built by developers from"
      />
      <div className="ts-builders">
        {BUILDER_LOGOS.map((logo) => (
          <LogoChip key={logo.file} logo={logo} />
        ))}
      </div>
    </div>
  </section>
);

const HeritageSection: React.FC = () => (
  <section className="ts-section" aria-labelledby="heritage-heading">
    <div className="ts-inner">
      <div className="ts-heritage">
        <p className="ts-header__eyebrow">Experience</p>
        <h2 id="heritage-heading" className="ts-header__heading">
          <span className="ts-heritage__highlight">10+ years of experience in AI.</span>
          <br />
          <span className="ts-header__sub">One of the first AI labs.</span>
        </h2>
        <p className="ts-heritage__copy">
          8080.AI is built by{' '}
          <a href={FXIS_URL} target="_blank" rel="noreferrer">fxis.ai</a>{' '}
          - F(x) Data Labs, Inc, founded in 2016 - an AI lab shipping data science
          and machine learning systems long before the AI wave. Every claim on this
          page is public and verifiable.
        </p>
        <div className="ts-heritage__stats">
          <div className="ts-stat">
            <span className="ts-stat__value">10+</span>
            <span className="ts-stat__label">Years in AI</span>
          </div>
          <div className="ts-stat">
            <span className="ts-stat__value">2016</span>
            <span className="ts-stat__label">Incorporated, on public record</span>
          </div>
          <div className="ts-stat">
            <span className="ts-stat__value">4.9/5</span>
            <span className="ts-stat__label">Clutch rating · 65 reviews</span>
          </div>
        </div>
        <div className="ts-heritage__actions">
          <a className="ts-btn ts-btn--primary" href={ZAUBA_URL} target="_blank" rel="noreferrer">
            Verify claim
            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
          </a>
          <a className="ts-btn ts-btn--ghost" href={FXIS_URL} target="_blank" rel="noreferrer">
            Visit fxis.ai
          </a>
        </div>
      </div>
    </div>
  </section>
);

interface CertDef {
  icon: string;
  title: string;
  body: string;
}

const CERTIFICATIONS: CertDef[] = [
  {
    icon: 'fa-solid fa-certificate',
    title: 'ISO 9001',
    body: 'Quality management system certified - a documented, audited process behind every release and every support ticket.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: 'ISO 27001',
    body: 'Information security management certified - risk assessment, access control and incident response are audited, not promised.',
  },
  {
    icon: 'fa-solid fa-file-shield',
    title: 'SOC 2',
    body: 'Independently attested controls for security, availability and confidentiality across the platform and the clusters that run your apps.',
  },
];

const COMPLIANCE_POINTS: CertDef[] = [
  {
    icon: 'fa-solid fa-dharmachakra',
    title: 'Independent Kubernetes stack',
    body: 'Your apps run on our own Kubernetes clusters in a namespace per project - not on a shared third-party app backend. Data, secrets and volumes stay inside your boundary.',
  },
  {
    icon: 'fa-solid fa-user-shield',
    title: 'GDPR ready',
    body: 'Namespace isolation, encrypted volumes, audit logs and per-project data deletion make both 8080.AI and the software it generates ready for GDPR compliance.',
  },
  {
    icon: 'fa-solid fa-notes-medical',
    title: 'HIPAA ready',
    body: 'The same isolated stack gives generated healthcare software an audited, access-controlled home - ready for a HIPAA compliance program and a signed BAA.',
  },
  {
    icon: 'fa-solid fa-magnifying-glass-chart',
    title: 'Security scan on every task',
    body: 'Before any task reaches staging it is scanned for leaked secrets, vulnerable dependencies and unprotected routes, and Deploy warns while findings remain open.',
  },
];

const CertCard: React.FC<{ cert: CertDef; badge?: boolean }> = ({ cert, badge }) => (
  <article className={`ts-cert${badge ? ' ts-cert--badge' : ''}`}>
    <i className={`ts-cert__icon ${cert.icon}`} aria-hidden="true" />
    <h3 className="ts-cert__title">{cert.title}</h3>
    <p className="ts-cert__body">{cert.body}</p>
  </article>
);

const ComplianceSection: React.FC = () => (
  <section id="compliance" className="ts-section" aria-labelledby="compliance-heading">
    <div className="ts-inner">
      <SectionHeader
        eyebrow="Security & compliance"
        headingId="compliance-heading"
        title="Certified. Isolated. Compliance-ready."
        sub="ISO 9001, ISO 27001 and SOC 2 - on an independent Kubernetes stack."
      />
      <div className="ts-certs">
        {CERTIFICATIONS.map((cert) => <CertCard key={cert.title} cert={cert} badge />)}
      </div>
      <div className="ts-compliance">
        {COMPLIANCE_POINTS.map((cert) => <CertCard key={cert.title} cert={cert} />)}
      </div>
      <p className="ts-compliance__note">
        Certifications cover the 8080.AI platform and its clusters. GDPR and HIPAA readiness describes the
        controls your generated software inherits; your own compliance program and agreements still apply.
      </p>
    </div>
  </section>
);

const Stars: React.FC = () => (
  <span className="ts-review__stars" aria-hidden="true">
    <i className="fa-solid fa-star" />
    <i className="fa-solid fa-star" />
    <i className="fa-solid fa-star" />
    <i className="fa-solid fa-star" />
    <i className="fa-solid fa-star" />
  </span>
);

const ReviewCard: React.FC<{ review: ReviewDef }> = ({ review }) => (
  <article className="ts-review">
    <div className="ts-review__rating">
      <Stars />
      <span className="ts-review__score">5.0</span>
    </div>
    <h3 className="ts-review__quote">&ldquo;{review.quote}&rdquo;</h3>
    <p className="ts-review__body">{review.detail}</p>
    <p className="ts-review__meta">{review.author}</p>
  </article>
);

const ReviewsSection: React.FC = () => (
  <section id="reviews" className="ts-section" aria-labelledby="reviews-heading">
    <div className="ts-inner">
      <SectionHeader
        eyebrow="Reviews"
        headingId="reviews-heading"
        title="Rated 4.9 out of 5 by clients."
        sub="Independently verified on Clutch."
      />
      <div className="ts-reviews__grid">
        {REVIEWS.map((review) => (
          <ReviewCard key={review.quote} review={review} />
        ))}
      </div>
      <div className="ts-reviews__footer">
        <a className="ts-btn ts-btn--ghost" href={CLUTCH_URL} target="_blank" rel="noreferrer">
          Read all 65 reviews on Clutch
          <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
        </a>
      </div>
    </div>
  </section>
);

export const TrustSections: React.FC = () => (
  <>
    <TrustedBrandsSection />
    <BuiltBySection />
    <HeritageSection />
    <ComplianceSection />
    <ReviewsSection />
  </>
);
